import { Link, useParams } from 'react-router-dom';
import { useAuxpow } from '../hooks/useAuxpow';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { ApiError } from '../api/client';
import type { AuxpowData } from '../api/types';
import { formatNumber, formatTimestamp, hex2ascii } from '../util/format';
import { MinerBadge } from '../components/MinerBadge';

export const AuxpowPage = () => {
  const { btcBlockHash } = useParams<{ btcBlockHash: string }>();

  if (!btcBlockHash || btcBlockHash.length !== 64) {
    return <ErrorMessage message="Invalid block hash" />;
  }

  const { data, loading, error, refetch } = useAuxpow(btcBlockHash);

  if (loading) {
    return <LoadingSpinner message="Loading AuxPoW data..." />;
  }

  if (error) {
    if (error instanceof ApiError && error.status === 404) {
      return (
        <ErrorMessage
          error={error}
          message={error.message}
          retry={refetch}
        />
      );
    }
    return <ErrorMessage error={error} retry={refetch} />;
  }

  if (!data || !data.auxpow) {
    return <ErrorMessage message="AuxPoW data not found" />;
  }

  const auxpow: AuxpowData = data.auxpow;
  const auxpowStatus = data.auxpow_anchor_status;
  const parent = data.parent;
  const utxoBlock = data.utxo_block;
  const syscoinOpReturn = data.syscoin_op_return;

  const isConfirmedOnBitcoin =
    !!auxpowStatus &&
    auxpowStatus.indexed === true &&
    !!auxpowStatus.btc_block_height;

  const parentIsBitcoinBlock = parent?.bitcoin_block === true;
  const depth = auxpowStatus?.depth ?? 0;

  const minerName =
    auxpowStatus?.miner_name || utxoBlock?.auxpow_miner || null;
  const minerPoolId =
    auxpowStatus?.miner_mempool_id || utxoBlock?.auxpow_miner_mempool_id;

  const btcHeader = auxpow.btc_header;

  return (
    <div className="auxpow-info">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center',
          marginBottom: '10px',
        }}
      >
        <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
          <h1 style={{ margin: 0, display: 'inline' }}>
            {isConfirmedOnBitcoin && auxpowStatus?.btc_block_height
              ? `Bitcoin Block #${formatNumber(auxpowStatus.btc_block_height)}`
              : 'AuxPoW Details'}
          </h1>
          {isConfirmedOnBitcoin && depth >= 0 && (
            <span style={{ fontSize: '0.9em', color: '#8b92a8', marginLeft: '10px' }}>
              ▪ {formatNumber(depth + 1)} confirmation
              {depth + 1 === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {isConfirmedOnBitcoin && auxpow.btc_block_hash && (
          <a
            href={`https://mempool.space/block/${auxpow.btc_block_hash}`}
            className="nav-button"
            target="_blank"
            rel="noopener"
          >
            View on mempool.space ↗
          </a>
        )}
      </div>

      <div className="info-grid">
        <div className="info-item">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '4px',
            }}
          >
            <strong>AuxPoW Hash:</strong>
            {isConfirmedOnBitcoin && (
              <span
                style={{
                  background: '#f7931a',
                  color: '#000',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '0.7em',
                  fontWeight: 'bold',
                  marginLeft: '8px',
                }}
              >
                Bitcoin block
              </span>
            )}
          </div>
          <code className="hash-display">
            {auxpow.btc_block_hash || btcBlockHash}
          </code>
        </div>

        {btcHeader && (
          <>
            <div className="info-item">
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  fontSize: '0.9em',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}
                >
                  <span
                    style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}
                  >
                    Version:
                  </span>
                  <span>{btcHeader.version}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}
                >
                  <span
                    style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}
                  >
                    Nonce:
                  </span>
                  <span>{btcHeader.nonce}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                  }}
                >
                  <span
                    style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}
                  >
                    Difficulty:
                  </span>
                  <span>
                    {btcHeader.bits
                      ? `0x${btcHeader.bits.toString(16)}`
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-item">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '4px',
                }}
              >
                <strong>
                  {isConfirmedOnBitcoin ? (
                    'Previous Block:'
                  ) : parent && parentIsBitcoinBlock ? (
                    <>
                      Parent{' '}
                      <span
                        style={{
                          background: '#f7931a',
                          color: '#000',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '0.7em',
                          fontWeight: 'bold',
                        }}
                      >
                        bitcoin
                      </span>{' '}
                      block:
                    </>
                  ) : (
                    'Parent Hash:'
                  )}
                </strong>
              </div>
              {parent ? (
                <Link
                  to={`/auxpow/${btcHeader.prev_block_hash}`}
                  style={{ textDecoration: 'none' }}
                >
                  <code className="hash-display hash-link">
                    {btcHeader.prev_block_hash}
                  </code>
                </Link>
              ) : (
                <code className="hash-display">
                  {btcHeader.prev_block_hash}
                </code>
              )}
              {!parent && (
                <div
                  style={{
                    fontSize: '0.8em',
                    color: '#8b92a8',
                    marginTop: '4px',
                  }}
                >
                  <em>Parent block not indexed yet</em>
                </div>
              )}
            </div>

            <div className="info-item">
              <strong>Merkle Root:</strong>
              <code className="hash-display">
                {btcHeader.merkle_root}
              </code>
            </div>
          </>
        )}

        {minerName && (
          <div className="info-item">
            <MinerBadge
              name={minerName}
              mempoolId={minerPoolId}
              nameColor={auxpowStatus?.miner_name ? '#f7931a' : '#81C784'}
              minedOn={
                btcHeader ? formatTimestamp(btcHeader.timestamp) : 'Unknown'
              }
            />
          </div>
        )}

        {utxoBlock?.hash && (
          <div className="info-item">
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'baseline',
              }}
            >
              <strong>
                UTXO Block #{formatNumber(utxoBlock.height || 0)}
              </strong>
              <span
                style={{
                  fontSize: '0.85em',
                  color: '#8b92a8',
                  whiteSpace: 'nowrap',
                }}
              >
                ▪ {formatNumber(utxoBlock.confirmations || 0)} confirmation
                {(utxoBlock.confirmations || 0) === 1 ? '' : 's'}
              </span>
            </div>
            <div style={{ marginTop: '6px' }}>
              <Link
                to={`/utxo/block/${utxoBlock.hash}`}
                style={{ textDecoration: 'none', display: 'inline-block' }}
              >
                <code className="hash-display hash-link">
                  {utxoBlock.hash}
                </code>
              </Link>
            </div>
          </div>
        )}
      </div>

      {isConfirmedOnBitcoin && (
        <>
          <h2 style={{ marginTop: '30px' }}>Bitcoin Finality</h2>
          <div
            className="navigation-card"
            style={{ borderColor: '#f7931a' }}
          >
            <p style={{ marginBottom: '10px' }}>
              <strong>Thresholds:</strong>
            </p>
            <div
              style={{
                background: '#0a0e27',
                padding: '15px',
                borderRadius: '6px',
                border: '1px solid #2a3354',
              }}
            >
              <div style={{ marginBottom: '12px' }}>
                <strong
                  style={{
                    color: depth >= 5 ? '#81C784' : '#8b92a8',
                  }}
                >
                  🔹 5 blocks (~50 min)
                </strong>
                <p
                  style={{
                    margin: '4px 0 0 24px',
                    fontSize: '0.9em',
                    color: '#c0c6d4',
                  }}
                >
                  Basic confirmation — typical for consumer payments
                </p>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong
                  style={{
                    color: depth >= 24 ? '#66BB6A' : '#8b92a8',
                  }}
                >
                  🔸 24 blocks (~4 hours)
                </strong>
                <p
                  style={{
                    margin: '4px 0 0 24px',
                    fontSize: '0.9em',
                    color: '#c0c6d4',
                  }}
                >
                  Exchange-grade security — deepest known rollback was ~24
                  blocks (2013 incident)
                </p>
              </div>
              <div>
                <strong
                  style={{
                    color: depth >= 144 ? '#4CAF50' : '#8b92a8',
                  }}
                >
                  🛡️ 144 blocks (~24 hours)
                </strong>
                <p
                  style={{
                    margin: '4px 0 0 24px',
                    fontSize: '0.9em',
                    color: '#c0c6d4',
                  }}
                >
                  Nuclear-safe finality — used by BitGo, Coinbase Prime for
                  $10M+ institutional custody
                </p>
              </div>
            </div>

            {depth >= 144 ? (
              <p
                style={{
                  marginTop: '15px',
                  color: '#4CAF50',
                  fontWeight: 'bold',
                }}
              >
                🛡️ This block has achieved nuclear-safe finality with {depth}{' '}
                Bitcoin blocks! Any rollback at this depth would represent a
                catastrophic Bitcoin consensus failure.
              </p>
            ) : depth >= 24 ? (
              <p
                style={{
                  marginTop: '15px',
                  color: '#66BB6A',
                }}
              >
                ✓ This block has reached exchange-grade security with {depth}{' '}
                Bitcoin blocks — safe for most institutional use cases.
              </p>
            ) : depth >= 5 ? (
              <p
                style={{
                  marginTop: '15px',
                  color: '#81C784',
                }}
              >
                ✓ This block is confirmed with {depth} Bitcoin blocks —{' '}
                {24 - depth} more for exchange-grade security.
              </p>
            ) : depth >= 1 ? (
              <p
                style={{
                  marginTop: '15px',
                  color: '#FFC107',
                }}
              >
                {depth} confirmation{depth > 1 ? 's' : ''} — {5 - depth} more
                for basic confirmation.
              </p>
            ) : (
              <p
                style={{
                  marginTop: '15px',
                  color: '#FFC107',
                }}
              >
                ⏳ Recently mined — no confirmations yet
              </p>
            )}
          </div>
        </>
      )}

      {syscoinOpReturn && auxpow.syscoin_commitment && (
        <>
          <h2 style={{ marginTop: '30px' }}>Syscoin Commitment</h2>
          <div
            className="navigation-card"
            style={{ borderColor: '#4a9eff' }}
          >
            {/* Responsive table layout */}
            <div className="syscoin-commitment-table">
              {/* Row 1: ASCII */}
              <div className="syscoin-commitment-cell syscoin-commitment-cell-left syscoin-commitment-cell-large syscoin-commitment-blue">
                <div className="syscoin-commitment-cell-label">Syscoin Tag</div>
                <div>sys</div>
              </div>
              <div className="syscoin-commitment-cell syscoin-commitment-cell-right syscoin-commitment-cell-large syscoin-commitment-orange">
                <div className="syscoin-commitment-cell-label">Block Hash (ASCII)</div>
                <div>
                  {(() => {
                    const asmOrHex = syscoinOpReturn.scriptpubkey_asm || syscoinOpReturn.scriptpubkey;
                    const ascii = hex2ascii(asmOrHex);
                    return ascii.substring(3) || '(binary data)';
                  })()}
                </div>
              </div>

              {/* Arrow Row 1 */}
              <div className="syscoin-commitment-arrow syscoin-commitment-arrow-left syscoin-commitment-blue">
                ↓
              </div>
              <div className="syscoin-commitment-arrow syscoin-commitment-arrow-right syscoin-commitment-orange">
                ↓
              </div>

              {/* Row 2: Hex */}
              <div className="syscoin-commitment-cell syscoin-commitment-cell-left syscoin-commitment-cell-small syscoin-commitment-blue">
                <div className="syscoin-commitment-cell-label">Tag</div>
                <div>737973</div>
              </div>
              <div className="syscoin-commitment-cell syscoin-commitment-cell-right syscoin-commitment-cell-small syscoin-commitment-orange">
                <div className="syscoin-commitment-label-row">
                  <span className="syscoin-commitment-cell-label">Big-Endian:</span>
                  <span className="syscoin-commitment-block-info">32-byte hash</span>
                </div>
                <div>{auxpow.syscoin_commitment.committed_hash}</div>
              </div>

              {/* Arrow Row 2 */}
              <div className="syscoin-commitment-arrow syscoin-commitment-arrow-left" style={{ color: '#8b92a8' }}>
                {/* Empty cell */}
              </div>
              <div className="syscoin-commitment-arrow syscoin-commitment-arrow-right syscoin-commitment-green">
                ↓
              </div>

              {/* Row 3: Little-endian */}
              <div className="syscoin-commitment-empty">
                {/* Empty cell */}
              </div>
              <div className="syscoin-commitment-cell syscoin-commitment-cell-right syscoin-commitment-cell-small syscoin-commitment-green">
                <div className="syscoin-commitment-label-row">
                  <span className="syscoin-commitment-cell-label">Little-Endian:</span>
                  {data.utxo_block ? (
                    <span className="syscoin-commitment-block-info">
                      UTXO Block #{formatNumber(data.utxo_block.height)} ▪ {formatNumber(data.utxo_block.confirmations)} confirmation{data.utxo_block.confirmations === 1 ? '' : 's'}
                    </span>
                  ) : (
                    <span className="syscoin-commitment-block-info" style={{ color: '#FFC107' }}>
                      ⚠️ Committed block not found in indexed data
                    </span>
                  )}
                </div>
                <div>
                  {auxpow.committed_utxo_hash ? (
                    <Link
                      to={`/utxo/block/${auxpow.committed_utxo_hash}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      {auxpow.committed_utxo_hash}
                    </Link>
                  ) : (
                    '(not found)'
                  )}
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: '20px',
                padding: '15px',
                borderLeft: '3px solid #4a9eff',
                borderRadius: '4px',
                fontSize: '0.9em',
                color: '#c0c6d4',
              }}
            >
              <strong>ℹ️ How it works:</strong>
              <ul
                style={{
                  margin: '8px 0 0 20px',
                  padding: 0,
                }}
              >
                <li style={{ marginBottom: '8px' }}>
                  The <strong>AuxPoW tag</strong> is embedded in Bitcoin&apos;s
                  coinbase transaction, cryptographically linking a Syscoin UTXO
                  block to Bitcoin&apos;s proof-of-work.
                </li>
                <li style={{ marginBottom: '8px' }}>
                  For stability, miners typically commit to UTXO blocks with approximately
                  10 confirmations (rather than the latest block).
                </li>
              </ul>
            </div>
          </div>
        </>
      )}

      {((auxpow.coinbase_branch && auxpow.coinbase_branch.length > 0) ||
        (auxpow.blockchain_branch && auxpow.blockchain_branch.length > 0)) && (
        <>
          <h2 style={{ marginTop: '30px' }}>🔗 Cryptographic Proof Chain</h2>
          <div className="navigation-card">
            <p
              style={{
                marginBottom: '20px',
                color: '#c0c6d4',
              }}
            >
              This AuxPoW contains merkle proof data that cryptographically
              links the Syscoin block to Bitcoin&apos;s proof-of-work. Each step
              below shows what is proven and the actual merkle hashes that form
              the proof.
            </p>

            {auxpow.blockchain_branch &&
              auxpow.blockchain_branch.length > 0 && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '20px',
                      marginBottom: '30px',
                      padding: '15px',
                      background: '#0a0e27',
                      borderRadius: '8px',
                      borderLeft: '3px solid #81C784',
                    }}
                  >
                    <div style={{ flex: '1 1 250px' }}>
                      <h3
                        style={{
                          fontSize: '1em',
                          color: '#81C784',
                          marginBottom: '10px',
                        }}
                      >
                        📌 Step 1: Syscoin → Coinbase
                      </h3>
                      <p
                        style={{
                          fontSize: '0.9em',
                          color: '#c0c6d4',
                          lineHeight: 1.5,
                        }}
                      >
                        Proves the Syscoin block hash is committed inside the
                        Bitcoin coinbase transaction using{' '}
                        {auxpow.blockchain_branch.length} merkle branch hash
                        {auxpow.blockchain_branch.length > 1 ? 'es' : ''}.
                      </p>
                      {auxpow.chain_index !== undefined && (
                        <p
                          style={{
                            fontSize: '0.85em',
                            color: '#8b92a8',
                            marginTop: '8px',
                          }}
                        >
                          Chain index: {auxpow.chain_index}
                        </p>
                      )}
                    </div>
                    <div style={{ flex: '2 1 400px' }}>
                      <div
                        style={{
                          fontSize: '0.85em',
                          color: '#8b92a8',
                          marginBottom: '8px',
                        }}
                      >
                        Blockchain Branch (
                        {auxpow.blockchain_branch.length} hash
                        {auxpow.blockchain_branch.length > 1 ? 'es' : ''}):
                      </div>
                      {auxpow.blockchain_branch.map((hash, idx) => (
                        <div
                          key={`bb-${idx}`}
                          style={{ marginBottom: '4px' }}
                        >
                          <code className="hash-display" style={{ color: '#81C784' }}>
                            [{idx}] {hash}
                          </code>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: 'center',
                      margin: '-15px 0 20px 0',
                    }}
                  >
                    <div
                      style={{ fontSize: '1.5em', color: '#4a9eff' }}
                    >
                      ↓
                    </div>
                  </div>
                </>
              )}

            {auxpow.coinbase_branch &&
              auxpow.coinbase_branch.length > 0 && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '20px',
                      marginBottom: '30px',
                      padding: '15px',
                      background: '#0a0e27',
                      borderRadius: '8px',
                      borderLeft: '3px solid #f7931a',
                    }}
                  >
                    <div style={{ flex: '1 1 250px' }}>
                      <h3
                        style={{
                          fontSize: '1em',
                          color: '#f7931a',
                          marginBottom: '10px',
                        }}
                      >
                        📌 Step 2: Coinbase → Merkle Root
                      </h3>
                      <p
                        style={{
                          fontSize: '0.9em',
                          color: '#c0c6d4',
                          lineHeight: 1.5,
                        }}
                      >
                        Proves the coinbase transaction is part of the Bitcoin
                        block&apos;s merkle tree using{' '}
                        {auxpow.coinbase_branch.length} merkle branch hash
                        {auxpow.coinbase_branch.length > 1 ? 'es' : ''},
                        connecting to the merkle root in the Bitcoin block
                        header.
                      </p>
                    </div>
                    <div style={{ flex: '2 1 400px' }}>
                      <div
                        style={{
                          fontSize: '0.85em',
                          color: '#8b92a8',
                          marginBottom: '8px',
                        }}
                      >
                        Coinbase Branch ({auxpow.coinbase_branch.length} hash
                        {auxpow.coinbase_branch.length > 1 ? 'es' : ''}):
                      </div>
                      {auxpow.coinbase_branch.map((hash, idx) => (
                        <div
                          key={`cb-${idx}`}
                          style={{ marginBottom: '4px' }}
                        >
                          <code className="hash-display" style={{ color: '#f7931a' }}>
                            [{idx}] {hash}
                          </code>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: 'center',
                      margin: '-15px 0 20px 0',
                    }}
                  >
                    <div
                      style={{ fontSize: '1.5em', color: '#4a9eff' }}
                    >
                      ↓
                    </div>
                  </div>
                </>
              )}

            {btcHeader?.merkle_root && (
              <div
                style={{
                  padding: '15px',
                  background: '#0a0e27',
                  borderRadius: '8px',
                  borderLeft: '3px solid #4CAF50',
                }}
              >
                <h3
                  style={{
                    fontSize: '1em',
                    color: '#4CAF50',
                    marginBottom: '10px',
                  }}
                >
                  ✓ Result: Bitcoin Block Header
                </h3>
                <p
                  style={{
                    fontSize: '0.9em',
                    color: '#c0c6d4',
                    marginBottom: '12px',
                  }}
                >
                  The merkle root is locked into the Bitcoin block header, which
                  meets the proof-of-work difficulty target.
                </p>
                <div>
                  <div
                    style={{
                      fontSize: '0.85em',
                      color: '#8b92a8',
                      marginBottom: '6px',
                    }}
                  >
                    Merkle Root:
                  </div>
                  <code className="hash-display" style={{ color: '#4CAF50' }}>
                    {btcHeader.merkle_root}
                  </code>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <h2 style={{ marginTop: '30px' }}>ℹ️ What is AuxPoW?</h2>
      <div className="navigation-card">
        <p style={{ marginBottom: '10px' }}>
          <strong>Auxiliary Proof of Work (AuxPoW)</strong> is a merge-mining
          technique that allows Syscoin to inherit Bitcoin&apos;s security.
        </p>
        <p style={{ marginBottom: '10px' }}>
          Bitcoin miners simultaneously mine Syscoin blocks by including a
          commitment in the Bitcoin block&apos;s coinbase transaction — to
          inherit Bitcoin&apos;s security through merge-mining without requiring
          additional energy.
        </p>
        <p style={{ marginBottom: '10px' }}>
          This page shows the AuxPoW data that cryptographically proves this
          Syscoin block was merge-mined:
        </p>
        <ul
          style={{
            marginLeft: '20px',
            marginBottom: '15px',
            color: '#c0c6d4',
          }}
        >
          <li>Bitcoin block header (80 bytes of pure PoW)</li>
          <li>Syscoin block hash commitment in coinbase</li>
          <li>Merkle branches proving the connection</li>
        </ul>
      </div>
    </div>
  );
};
